import {
  Maximize,
  Minimize,
  Pause,
  Play,
  Volume2,
  VolumeX,
} from "lucide-react";

interface VideoControlBarProps {
  visible: boolean;
  paused: boolean;
  muted: boolean;
  onTogglePlay: () => void;
  onToggleMute: () => void;
  /** omit to hide the fullscreen button */
  onToggleFullscreen?: () => void;
  fullscreenActive?: boolean;
  /** live stream: LIVE badge instead of a seek bar */
  live?: boolean;
  currentTime?: number;
  duration?: number;
  onSeek?: (time: number) => void;
}

const formatTime = (seconds: number) => {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const s = Math.floor(seconds % 60);
  const m = Math.floor((seconds / 60) % 60);
  const h = Math.floor(seconds / 3600);
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
};

const BUTTON_CLASS =
  "flex h-10 w-10 shrink-0 items-center justify-center rounded-md hover:bg-white/15 focus-visible:outline-2 focus-visible:outline-white sm:h-8 sm:w-8";

/**
 * Custom video controls used instead of the native ones: browsers resize
 * native controls in fullscreen and Chrome adds a ⋮ menu that can't be
 * hidden, so buttons placed next to them never line up.
 */
const VideoControlBar = ({
  visible,
  paused,
  muted,
  onTogglePlay,
  onToggleMute,
  onToggleFullscreen,
  fullscreenActive = false,
  live = false,
  currentTime = 0,
  duration = 0,
  onSeek,
}: VideoControlBarProps) => {
  const canSeek =
    !live && !!onSeek && Number.isFinite(duration) && duration > 0;

  return (
    <div
      className={`absolute inset-x-0 bottom-0 z-[100] bg-gradient-to-t from-black/80 to-transparent px-2 pb-1 pt-8 text-white transition-opacity duration-300 sm:px-3 sm:pb-2 ${
        visible ? "opacity-100" : "pointer-events-none opacity-0"
      }`}
    >
      {canSeek && (
        <input
          type="range"
          min={0}
          max={duration}
          step={0.1}
          value={Math.min(currentTime, duration)}
          onChange={(e) => onSeek(Number(e.target.value))}
          aria-label="Seek"
          className="mb-1 block h-1 w-full cursor-pointer accent-white"
        />
      )}
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onTogglePlay}
          aria-label={paused ? "Play" : "Pause"}
          className={BUTTON_CLASS}
        >
          {paused ? <Play size={18} /> : <Pause size={18} />}
        </button>
        {live ? (
          <span className="ml-1 flex items-center gap-1.5 text-xs font-semibold tracking-wide">
            <span className="h-2 w-2 rounded-full bg-red-500" />
            <span>LIVE</span>
          </span>
        ) : (
          <span className="ml-1 text-xs tabular-nums">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>
        )}
        <button
          type="button"
          onClick={onToggleMute}
          aria-label={muted ? "Unmute" : "Mute"}
          className={`ml-auto ${BUTTON_CLASS}`}
        >
          {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
        </button>
        {onToggleFullscreen && (
          <button
            type="button"
            onClick={onToggleFullscreen}
            aria-label={fullscreenActive ? "Exit fullscreen" : "Fullscreen"}
            className={BUTTON_CLASS}
          >
            {fullscreenActive ? <Minimize size={18} /> : <Maximize size={18} />}
          </button>
        )}
      </div>
    </div>
  );
};

export default VideoControlBar;
